// Lightweight wrapper around Google Analytics (gtag).
//
// gtag is only present after the visitor accepts cookies (see CookieConsent.tsx,
// which injects the gtag.js script and defines window.gtag). Until then — and for
// visitors who decline — these helpers are no-ops, so tracking always respects
// the cookie banner without each caller having to check.

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Send a GA4 event, if analytics has been loaded (i.e. cookies were accepted).
 * Safe to call anywhere — it silently does nothing when gtag isn't available.
 *
 * @param eventName GA4 event name, e.g. 'generate_lead'
 * @param params    Optional event parameters
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

/**
 * Start tracking clicks on phone (tel:) and email (mailto:) links anywhere on
 * the site, via a single delegated listener. For a law firm these are the main
 * conversions, so making them visible matters more than contact-page visits.
 * Fires GA4 events 'click_call' / 'click_email' (no-ops without consent).
 *
 * Returns a cleanup function that removes the listener.
 */
export const startContactClickTracking = (): (() => void) => {
  const handler = (e: MouseEvent) => {
    const link = (e.target as HTMLElement | null)?.closest?.('a');
    const href = link?.getAttribute('href') || '';
    if (href.startsWith('tel:')) {
      trackEvent('click_call', { phone_number: href.slice(4) });
    } else if (href.startsWith('mailto:')) {
      trackEvent('click_email', { email_address: href.slice(7) });
    }
  };
  document.addEventListener('click', handler);
  return () => document.removeEventListener('click', handler);
};
